import React, { useState } from 'react';
import { calculateDocumentStatus } from '../../utils/documentStatus';
import { 
  FiFileText, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiPaperclip, 
  FiChevronDown, 
  FiChevronUp, 
  FiClock, 
  FiAlertTriangle, 
  FiHelpCircle 
} from 'react-icons/fi';

const statusBadge = {
  'À fournir': 'bg-red-100 text-red-800 border-red-200',
  'En attente': 'bg-orange-100 text-orange-800 border-orange-200',
  'Fourni partiellement': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Fourni': 'bg-green-100 text-green-800 border-green-200',
  'Validé': 'bg-green-100 text-green-800 border-green-200',
  'Rejeté': 'bg-red-100 text-red-800 border-red-200',
  'Optionnel': 'bg-sky-100 text-sky-800 border-sky-200',
};

const DocumentSection = ({ 
  document,
  pdl,
  fournisseurs,
  userProfile
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const toggleOpen = () => setIsOpen(!isOpen);

  const isOptional = document.id === 'contrat_fourniture';
  const currentDisplayStatus = isOptional ? 'Optionnel' : calculateDocumentStatus(document);
  const currentBadgeClass = statusBadge[currentDisplayStatus] || statusBadge['À fournir'];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Validé':
        return { icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-50' };
      case 'Fourni':
      case 'En attente':
        return { icon: FiClock, color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'Rejeté':
        return { icon: FiAlertTriangle, color: 'text-red-600', bg: 'bg-red-50' };
      case 'Optionnel':
        return { icon: FiHelpCircle, color: 'text-gray-500', bg: 'bg-gray-50' };
      default:
        return { icon: FiAlertCircle, color: 'text-orange-600', bg: 'bg-orange-50' };
    }
  };

  const statusConfig = getStatusConfig(currentDisplayStatus);
  const StatusIcon = statusConfig.icon;
  const fileCount = document.files?.length || 0;

  // Récupération des infos fournisseur (lecture seule)
  const fournisseurInfo = document.userAnswerFournisseurId 
    ? fournisseurs.find(f => f.id === document.userAnswerFournisseurId)
    : null;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200" id={`doc-${document.id}`}>
      <button 
        onClick={toggleOpen} 
        className="w-full flex justify-between items-center p-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-t-xl hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start space-x-4">
          <div className={`p-2 rounded-full ${statusConfig.bg}`}>
            <StatusIcon className={`h-5 w-5 ${statusConfig.color}`} />
          </div>
          <div className="text-left">
            <h2 className="text-xl font-bold text-gray-900">
              {document.name}
              {isOptional && <span className="text-sm font-normal text-gray-500 ml-2">(Optionnel)</span>}
            </h2>
            <div className="flex items-center space-x-4 mt-2">
              <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full border ${currentBadgeClass}`}>
                {currentDisplayStatus}
              </span>
              <span className="flex items-center text-sm text-gray-500">
                <FiPaperclip className="h-4 w-4 mr-1" />
                {fileCount} fichier{fileCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
        {isOpen ? <FiChevronUp className="text-gray-400 h-6 w-6" /> : <FiChevronDown className="text-gray-400 h-6 w-6" />}
      </button>

      {isOpen && (
        <div id={`content-${document.id}`} className="p-6 border-t border-gray-200">
          {/* Description du document */}
          <div className="text-sm text-gray-600 space-y-2 mb-6">
            {(document.description || document.detailedDescription || []).map((line, index) => 
              <p key={index} dangerouslySetInnerHTML={{ __html: line }} />
            )}
          </div>

          {/* Informations de configuration (lecture seule) */}
          {document.id === 'factures_electricite' && (
            <div className="mb-6 p-4 border border-blue-200 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Configuration de la facturation</h3>
              
              <div className="space-y-3">
                {fournisseurInfo && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Fournisseur : </span>
                    <span className="text-sm text-gray-900">{fournisseurInfo.name}</span>
                  </div>
                )}
                
                {document.userAnswerPeriodicite && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Périodicité : </span>
                    <span className="text-sm text-gray-900 capitalize">{document.userAnswerPeriodicite}</span>
                  </div>
                )}

                {userProfile?.selected_offer && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Niveau d'analyse : </span>
                    <span className="text-sm text-gray-900 capitalize">{userProfile.selected_offer}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Liste des fichiers uploadés (lecture seule) */}
          {document.files && document.files.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Fichiers soumis :</h4>
              <div className="space-y-3">
                {document.files.map((file, index) => (
                  <div 
                    key={file.storagePath || `${file.name}-${index}`} 
                    className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center space-x-3">
                      {file.status === "Validé" ? 
                        <FiCheckCircle className="text-green-500 h-5 w-5" /> : 
                        file.status === "Rejeté" ? 
                        <FiAlertCircle className="text-red-500 h-5 w-5" /> : 
                        <FiFileText className="text-gray-500 h-5 w-5" />
                      }
                      <div>
                        <span className="text-gray-900 font-medium">{file.name}</span>
                        <span className="text-gray-500 ml-2 text-sm">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                        {file.period && (
                          <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {file.period}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Badge de statut */}
                    {file.status && (
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        file.status === 'Validé' ? 'bg-green-100 text-green-800' :
                        file.status === 'Rejeté' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {file.status}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message si aucun fichier */}
          {(!document.files || document.files.length === 0) && (
            <div className="p-4 text-center bg-gray-50 text-gray-500 rounded-lg border border-gray-200">
              <FiAlertCircle className="inline mr-2 h-5 w-5" />
              Aucun fichier soumis pour ce document
            </div>
          )}

          {/* Raison de rejet (si applicable) */}
          {document.status === "Rejeté" && document.rejectionReason && (
            <div className="mt-4 p-4 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-start">
                <FiAlertTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Motif du rejet :</strong> {document.rejectionReason}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentSection;
